<template>
  <WdsStack v-if="!loading && hire" direction-column gap-md class="tw-px-2">
    <WdsStack v-if="formatEndDate !== null" class="tw-w-full">
      <WdsProgressBar
        :progress="progressPercentage"
        :progress-excess-threshold="terminationDate !== null ? 0 : 100"
        class="tw-w-full hire-progress"
        :class="{ 'hire-progress--terminated': terminationDate !== null }"
      >
        <template #progressInfo>
          <WdsStack justify-space-between>
            <WdsStack gap-custom="5px">
              <WdsText>{{ $t('Ends :date', { date: formatEndDate }) }}</WdsText>
              <WdsText v-if="daysRemaining !== null" secondary>
                /
                {{ $tc('in :count day | in :count days', daysRemaining) }}
              </WdsText>
            </WdsStack>
            <WdsText v-if="totalTenureDays !== null && totalTenureDays > 0">
              {{ $tc('Tenure: :count day | Tenure: :count days', totalTenureDays) }}
            </WdsText>
          </WdsStack>
        </template>
      </WdsProgressBar>
    </WdsStack>
    <WdsStack v-else justify-space-between class="tw-w-full">
      <WdsStack gap-custom="5px">
        <WdsText>{{ $t('No end date') }}</WdsText>
      </WdsStack>
      <WdsText>
        {{ $tc('Tenure: :count day | Tenure: :count days', totalTenureDays) }}
      </WdsText>
    </WdsStack>
    <WdsAlert
      v-if="terminationDate !== null"
      warning
      icon
      bordered
      data-test="hire-ending-early-alert"
    >
      <WdsStack direction-column>
        <span>{{ $t('Ending early on :date', { date: terminationDate }) }}</span>
        <WdsText
          :text="
            $t(
              'This contract was terminated early. Payment requests can still be submitted for the adjusted contract period.',
            )
          "
        />
      </WdsStack>
    </WdsAlert>
    <slot name="action"></slot>
  </WdsStack>
  <PendingContractAcceptanceAlert :hire-id="hireId" class="tw-px-2 tw-mt-4 tw-mb-2" />
  <HireComplianceGateStatus
    :compliances="displayedCompliances"
    :loading="complianceLoading"
    :hire-id="hireId"
  >
    <template
      v-if="hasUnapprovedPaymentRequests || hire?.endsWithinDays || companyNeedsVerificationReview"
      #hire-actions
    >
      <FeatureFlagged v-if="hasUnapprovedPaymentRequests" name="hire-page-payment-requests-tab">
        <HireAlert
          :title="$t('Payment request pending')"
          :icon="WdsIconNameEnum.RECEIPT"
          :button-text="$t('Review [assess]')"
          status="warning"
          @click="router.push(`/hires/${hireId}/payments`)"
        />
        <template #is-disabled>
          <HireAlert
            :title="$t('Payment request pending')"
            :icon="WdsIconNameEnum.RECEIPT"
            :href="`${laravelNamedRoute('billing.fmsBills')}?bill-status=UNAPPROVED`"
            :button-text="$t('Review [assess]')"
            status="warning"
          />
        </template>
      </FeatureFlagged>
      <HireAlert
        v-if="hire?.endsWithinDays"
        :title="$t('Contract is ending soon')"
        :description="$t('You can extend the contract, if more time is needed.')"
        :icon="WdsIconNameEnum.INFO"
        :button-text="$t('Extend')"
        status="none"
        :action="extendContract"
      />
      <HireComplianceAlert
        v-if="companyNeedsVerificationReview && verifiedWorkerCompliance"
        :compliance="verifiedWorkerCompliance"
        :hire-id="hireId"
        data-test="awaiting-verification-review-action"
      />
    </template>
    <template v-if="awaitingVerificationReReview" #worker-actions>
      <HireAlert
        :title="$t('Awaiting re-verification')"
        :description="$t('The company is reviewing your compliance again before this extension can be accepted.')"
        :icon="WdsIconNameEnum.INFO"
        status="warning"
      />
    </template>
  </HireComplianceGateStatus>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import {
  WdsAlert,
  WdsIconNameEnum,
  WdsProgressBar,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import moment from 'moment'
import HireAlert from '../HireAlert.vue'
import PendingContractAcceptanceAlert from '../PendingContractAcceptanceAlert.vue'
import { useContractDrawer } from '../composables/useContractDrawer'
import HireComplianceAlert from './HireComplianceAlert.vue'
import { graphql } from '@/gql'
import { ComplianceName } from '@/gql/graphql'
import HireComplianceGateStatus from '@/components/Hire/HirePageCompliance/HireComplianceGateStatus.vue'
import { $t, $tc } from '@/app-bootstrapping/SetUpInternationalization'
import useDate from '@/composables/useDate'
import { useComplianceGate } from '@/components/Hire/HirePageCompliance/useComplianceGates'
import activeCompliances from '@/components/Hire/HirePageCompliance/activeCompliances'
import FeatureFlagged from '@/components/FeatureFlagged.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'

const router = useRouter()

const props = defineProps<{
  hireId: string
}>()

const { result, loading } = useQuery(
  graphql(`
    query HireActiveStatus($id: ID!) {
      hire(id: $id) {
        id
        startDate
        endDate
        terminationDate
        endsWithinDays
        paymentRequests(statuses: [UNAPPROVED]) {
          paginatorInfo {
            total
          }
        }
      }
    }
  `),
  {
    id: props.hireId,
  },
  {
    clientId: 'publicApi',
  },
)

const hire = computed(() => result.value?.hire)

const hasUnapprovedPaymentRequests = computed(
  () => (hire.value?.paymentRequests?.paginatorInfo?.total ?? 0) > 0,
)

const formatEndDate = computed(() => {
  if (!hire.value?.endDate) return null
  return useDate(hire.value.endDate, 'MMM DD, YYYY').date.value
})

const terminationDate = computed(() => {
  if (!hire.value?.terminationDate) return null
  return useDate(hire.value.terminationDate, 'MMM DD, YYYY').date.value
})

const daysRemaining = computed(() => {
  if (!hire.value?.endDate) return null
  const endDate = moment(hire.value.endDate)
  const today = moment()
  const daysDiff = endDate.diff(today, 'days')
  return daysDiff > 0 ? daysDiff : 0
})

const totalTenureDays = computed(() => {
  if (!hire.value?.startDate) return null
  const startDate = moment(hire.value.startDate)
  const today = moment()

  // If no end date or end date is in the future, calculate days from start to today
  if (!hire.value?.endDate) {
    return today.diff(startDate, 'days')
  }

  const endDate = moment(hire.value.endDate)

  // If end date is after today, calculate days from start to today
  if (endDate.isAfter(today)) {
    return today.diff(startDate, 'days')
  }

  // Otherwise, calculate days from start to end date
  return endDate.diff(startDate, 'days')
})

const progressPercentage = computed(() => {
  if (!hire.value?.startDate || !hire.value?.endDate) return 0
  const startDate = moment(hire.value.startDate)
  const endDate = moment(hire.value.endDate)
  const today = moment()

  const totalTime = endDate.diff(startDate, 'days')
  const elapsedTime = today.diff(startDate, 'days')

  if (elapsedTime <= 0) return 0
  if (elapsedTime >= totalTime) return 100

  return Math.round((elapsedTime / totalTime) * 100)
})

const { active, loading: complianceLoading } = useComplianceGate(
  props.hireId,
  ComplianceName.GateHireCreateBill,
)

// Narrow to one compliance — the full accept-gate query walks every
// row and is noticeably slow.
const { result: verificationResult } = useQuery(
  graphql(`
    query VerifiedWorkerComplianceForActiveHire($id: ID!) {
      compliance(id: $id, names: [VERIFIED_WORKER_COMPLIANCE]) {
        ...ComplianceAlert
      }
    }
  `),
  () => ({ id: props.hireId }),
  { clientId: 'publicApi' },
)

const { isWorker } = useAccountCapabilities()

// Surfaced as an actionable alert in #hire-actions (company-only by
// slot) and as a passive banner in #worker-actions below.
const verifiedWorkerCompliance = computed(
  () => activeCompliances(verificationResult.value?.compliance ?? [])[0] ?? null,
)

const companyNeedsVerificationReview = computed(
  () => verifiedWorkerCompliance.value !== null && !verifiedWorkerCompliance.value.completed,
)

// Hide while the worker can't act — the top alert already shows the
// pending change with a properly disabled button.
const displayedCompliances = computed(() =>
  companyNeedsVerificationReview.value
    ? active.value.filter((c) => c.name !== ComplianceName.ContractChanges)
    : active.value,
)

// Worker-only — the copy is addressed to them, and #worker-actions
// otherwise leaks into the company and SA views.
const awaitingVerificationReReview = computed(
  () => isWorker.value && companyNeedsVerificationReview.value,
)

const { openContractDrawer } = useContractDrawer()

const extendContract = () => {
  openContractDrawer('edit')
}
</script>

<style scoped>
.hire-progress:not(.hire-progress--terminated) :deep(.wds-pb__bar--progress) {
  background-color: #7acc8d !important;
}
</style>
